import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { useAppSelector } from "../../app/hooks";
import { RouteRequestInput, RouteRequestState, Trip } from "../../generated/gpb/api/route";
import { RouteId } from "../../generated/gpb/api/id";

export const DrawerStateKinds = {
    Closed: "closed",
    TripLibrary: "tripLibrary",
    UserProfileDrawer: "userProfileDrawer",
    TripInput: "tripInput",
    Edit: "edit",
    TripResults: "tripResults",
    MultistartSelection: "multistartSelection"
} as const;

export type DrawerState =
  | { readonly kind: typeof DrawerStateKinds.Closed }
  | { readonly kind: typeof DrawerStateKinds.TripLibrary }
  | { readonly kind: typeof DrawerStateKinds.UserProfileDrawer }
  | { readonly kind: typeof DrawerStateKinds.TripInput, selectedWaypointInputIndex: undefined | number, isAdvisory: boolean }
  | { readonly kind: typeof DrawerStateKinds.Edit, selectedWaypointInputIndex: undefined | number, readonly trip: Trip, readonly routeRequestInput: RouteRequestInput, isAdvisory: boolean, isForecastUpdate: boolean }
  | { readonly kind: typeof DrawerStateKinds.TripResults, readonly trip: Trip, readonly routeId: RouteId | undefined }
  | { readonly kind: typeof DrawerStateKinds.MultistartSelection, readonly trip: Trip, readonly routeId: RouteId | undefined };

const initialDrawerState: DrawerState = {
    kind: DrawerStateKinds.TripLibrary
};

export const drawerSlice = createSlice( {
    name: "drawer",
    initialState: initialDrawerState as DrawerState,
    reducers: {
        renameTripAction( state, action: PayloadAction<string> )
        {
            if ( state.kind === DrawerStateKinds.TripResults )
            {
                state = { ...state, trip: { ...state.trip, name: action.payload } };
            }
        },
        closeDrawer( state )
        {
            state.kind = DrawerStateKinds.Closed;
        },
        openTripLibrary( state )
        {
            state.kind = DrawerStateKinds.TripLibrary;
        },
        openUserProfileDrawer( state )
        {
            state.kind = DrawerStateKinds.UserProfileDrawer;
        },
        openTripInput( state )
        {
            state.kind = DrawerStateKinds.TripInput;
            if ( state.kind === DrawerStateKinds.TripInput )
            {
                state.selectedWaypointInputIndex = 0;
            }
            else if ( state.kind === DrawerStateKinds.Edit )
            {
                state.selectedWaypointInputIndex = 0;
            }
        },
        openMultistartSelection( state, action: PayloadAction<{ trip: Trip, routeId: RouteId | undefined }> )
        {
            state.kind = DrawerStateKinds.MultistartSelection;
            ( state as { kind: typeof DrawerStateKinds.MultistartSelection, trip: Trip, routeId: RouteId | undefined } ).trip = action.payload.trip;
            ( state as { kind: typeof DrawerStateKinds.MultistartSelection, trip: Trip, routeId: RouteId | undefined } ).routeId = action.payload.routeId;
        },
        openTripResults( state, action: PayloadAction<{ trip: Trip, routeId: RouteId | undefined }> )
        {
            state.kind = DrawerStateKinds.TripResults;
            ( state as { kind: typeof DrawerStateKinds.TripResults, trip: Trip, routeId: RouteId | undefined } ).trip = action.payload.trip;
            ( state as { kind: typeof DrawerStateKinds.TripResults, trip: Trip, routeId: RouteId | undefined } ).routeId = action.payload.routeId;
        },
        multistartToTripResults( state: DrawerState, action: PayloadAction<{ routeId: RouteId | undefined }> )
        {
            if ( state.kind === DrawerStateKinds.MultistartSelection )
            {
                const newtrip = {
                    ...state.trip,
                    routeState: RouteRequestState.ROUTE_SELECTED
                };

                return {
                    ...state,
                    trip: newtrip,
                    routeId: action.payload.routeId,
                    kind: DrawerStateKinds.TripResults,
                } as DrawerState;
            }

            return state;
        },
        updateTripRouteId( state, action: PayloadAction<{ routeId: RouteId | undefined }> )
        {
            if ( state.kind === DrawerStateKinds.TripResults )
            {
                ( state as { kind: typeof DrawerStateKinds.TripResults, trip: Trip, routeId: RouteId | undefined } ).routeId = action.payload.routeId;
            }
            if ( state.kind === DrawerStateKinds.MultistartSelection )
            {
                ( state as { kind: typeof DrawerStateKinds.MultistartSelection, trip: Trip, routeId: RouteId | undefined } ).routeId = action.payload.routeId;
            }
        },
        openEdit( state, action: PayloadAction<{ trip: Trip, routeRequestInput: RouteRequestInput, isForecastUpdate: boolean }> )
        {
            state.kind = DrawerStateKinds.Edit;
            ( state as { kind: typeof DrawerStateKinds.Edit, trip: Trip, routeRequestInput: RouteRequestInput, isForecastUpdate: boolean } ).trip = action.payload.trip;
            ( state as { kind: typeof DrawerStateKinds.Edit, trip: Trip, routeRequestInput: RouteRequestInput, isForecastUpdate: boolean } ).isForecastUpdate = action.payload.isForecastUpdate;
            ( state as { kind: typeof DrawerStateKinds.Edit, trip: Trip, routeRequestInput: RouteRequestInput, isForecastUpdate: boolean } ).routeRequestInput = action.payload.routeRequestInput;
        },
        resetState()
        {
            return initialDrawerState;
        },
        setSelectedWaypointInputIndex( state, action: PayloadAction<number> )
        {
            if ( state.kind === DrawerStateKinds.TripInput || state.kind === DrawerStateKinds.Edit )
            {
                state.selectedWaypointInputIndex = action.payload;
            }
        }
    },
} );

const selectDrawerState = ( state: RootState ) =>
{
    const { kind } = state.drawer;
    switch ( kind )
    {
        case DrawerStateKinds.Closed:
            return { kind };
        case DrawerStateKinds.TripLibrary:
            return { kind };
        case DrawerStateKinds.UserProfileDrawer:
            return { kind };
        case DrawerStateKinds.TripInput:
            return { kind };
        case DrawerStateKinds.TripResults:
            return { kind, trip: state.drawer.trip, routeId: state.drawer.routeId };
        case DrawerStateKinds.MultistartSelection:
            return { kind, trip: state.drawer.trip, routeId: state.drawer.routeId };
        case DrawerStateKinds.Edit:
            return { kind, trip: state.drawer.trip, routeRequestInput: state.drawer.routeRequestInput, isForecastUpdate: state.drawer.isForecastUpdate };
        default:
            return state.drawer;
    }
};

export const useIsRightBarExpanded = () =>
{
    const drawerStateKind = useAppSelector( ( state: RootState ) => state.drawer.kind );
    return drawerStateKind === DrawerStateKinds.UserProfileDrawer;
};

export const useIsLeftBarExpanded = () =>
{
    const drawerStateKind = useAppSelector( ( state: RootState ) => state.drawer.kind );
    return drawerStateKind === DrawerStateKinds.TripLibrary ||
           drawerStateKind === DrawerStateKinds.TripInput ||
           drawerStateKind === DrawerStateKinds.TripResults ||
           drawerStateKind === DrawerStateKinds.MultistartSelection ||
           drawerStateKind === DrawerStateKinds.Edit;
};
export const useSelectedWaypointInputIndex = () => useAppSelector( ( state: RootState ) =>
{
    if ( state.drawer.kind === DrawerStateKinds.TripInput ) return state.drawer.selectedWaypointInputIndex;
    if ( state.drawer.kind === DrawerStateKinds.Edit ) return state.drawer.selectedWaypointInputIndex;
    return undefined;
} );
export const useTripIdAndRouteRequestIndex = () => useAppSelector( ( state: RootState ) =>
{
    if ( state.drawer.kind === DrawerStateKinds.MultistartSelection || state.drawer.kind === DrawerStateKinds.TripResults )
    {
        return { tripId: state.drawer.trip.Id };
    }
    return undefined;
} );
export const useDrawerState = () => useAppSelector( selectDrawerState );
export const {
    closeDrawer,
    openTripLibrary,
    openUserProfileDrawer,
    openTripInput,
    openTripResults,
    openMultistartSelection,
    updateTripRouteId,
    multistartToTripResults,
    openEdit,
    resetState,
    setSelectedWaypointInputIndex,
    renameTripAction
} = drawerSlice.actions;
export default drawerSlice.reducer;
